import {
  ApprovalMode,
  ApprovalRule,
  EntityApproverStatus,
  EntityTransitionRequest,
  WorkflowTransition,
} from '../types';

/** One approver's decision on a request. */
export interface MockApprovalDecision {
  approverId: string;
  decidedAt: string;
  decidedByDisplayName: string;
}

/**
 * A card's open transition request as the fixture keeps it. The rule is the
 * one in force when the request was made — a later publish does not change
 * what an open request waits for.
 */
export interface MockTransitionRequest {
  id: string;
  transitionKey: string;
  toStatusKey: string;
  rule: ApprovalRule;
  decisions: MockApprovalDecision[];
  createdAt: string;
  createdByDisplayName: string;
  /** The target status asks for a step form, so a person has to move it. */
  targetHasStepForm: boolean;
}

/**
 * The transition a card moving from `fromStatusKey` to `toStatusKey` takes:
 * one declared from that status wins over one allowed from any status.
 */
export const findTransition = (
  transitions: WorkflowTransition[],
  fromStatusKey: string,
  toStatusKey: string,
) => {
  const leading = transitions.filter(
    transition => transition.toStatusKey === toStatusKey,
  );

  return (
    leading.find(transition => transition.fromStatusKey === fromStatusKey) ??
    leading.find(transition => transition.fromStatusKey === null)
  );
};

/** Whether a rule asks for anyone's approval at all. */
export const requiresApproval = (
  rule: ApprovalRule | null | undefined,
): rule is ApprovalRule => !!rule?.approvers.length;

/**
 * Where a request stands. The fixture has no identities, so the current user
 * may approve for any approver still pending.
 */
export const toTransitionRequest = (
  request: MockTransitionRequest,
): EntityTransitionRequest => {
  const { rule, decisions } = request;
  const approvers = rule.approvers.map(approver => {
    const decision = decisions.find(item => item.approverId === approver.id);

    return {
      approver,
      status: decision
        ? EntityApproverStatus.Approved
        : EntityApproverStatus.Pending,
      decidedAt: decision?.decidedAt ?? null,
      decidedByDisplayName: decision?.decidedByDisplayName ?? null,
    };
  });
  const approvedCount = approvers.filter(
    item => item.status === EntityApproverStatus.Approved,
  ).length;
  const requiredCount =
    rule.mode === ApprovalMode.All ? rule.approvers.length : 1;
  const isApproved = approvedCount >= requiredCount;

  return {
    id: request.id,
    transitionKey: request.transitionKey,
    toStatusKey: request.toStatusKey,
    createdAt: request.createdAt,
    createdByDisplayName: request.createdByDisplayName,
    mode: rule.mode,
    finalCheckByLastApprover: rule.finalCheckByLastApprover,
    requiredCount,
    approvedCount,
    approvers,
    isApproved,
    awaitingFinalCheck: isApproved && rule.finalCheckByLastApprover,
    awaitingMove:
      isApproved &&
      (rule.finalCheckByLastApprover || request.targetHasStepForm),
    canApprove:
      !isApproved &&
      approvers.some(item => item.status === EntityApproverStatus.Pending),
  };
};
